import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AdminPortalAccountService } from '../admin-portal-account.service';
import { IUserEntity } from 'src/app/core/model/server/vo/IUserEntity.interface';
import { UpdateUserRequest } from 'src/app/core/model/server/vo/UpdateUserRequest.interface';
import { CreateUserRequest } from 'src/app/core/model/server/vo/CreateUserRequest.interface';
import { UserService } from 'src/app/core/services/user.service';

export interface AccountDialogData {
  mode: 'create' | 'edit';
  user?: IUserEntity;
}

interface AccountTypeOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-portal-account-modal',
  templateUrl: './portal-account-modal.component.html',
  styleUrl: './portal-account-modal.component.scss',
})
export class AdminPortalAccountModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private originalValues: any = null;

  form!: UntypedFormGroup;
  isEditMode = false;
  isAdmin: boolean;
  apiError: string | null = null;

  accountTypeOptions: AccountTypeOption[] = [
    { label: 'Super Admin', value: 'Super Admin' },
    { label: 'Admin',       value: 'Admin'       },
    { label: 'Operations',  value: 'Operations'         },
    { label: 'Audit',       value: 'Audit'        },
    { label: 'Marketing',   value: 'Marketing'     },
    { label: 'Accounting',  value: 'Accounting'  },
  ];

  selectedAccountType: AccountTypeOption | null = null;
  
  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<AdminPortalAccountModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AccountDialogData,
    private portalAccountService: AdminPortalAccountService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private userService: UserService
  ) {}
  
  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';
    this.isAdmin = this.portalAccountService.isAdmin;
    this.form = this.fb.group({
      username:    ['', [Validators.required, Validators.minLength(5), Validators.pattern('^[a-zA-Z0-9_]+$')]],
      firstName:   ['', [Validators.required, Validators.pattern('^[a-zA-Z ]+$')]],
      lastName:    ['', [Validators.required, Validators.pattern('^[a-zA-Z ]+$')]],
      email:       ['', [Validators.required, Validators.email]],
      accountType: ['', Validators.required],
    });

    if (this.isEditMode && !this.portalAccountService.isAdmin) {
      this.form.get('username')?.clearValidators();
      this.form.get('username')?.updateValueAndValidity();
      this.form.get('accountType')?.clearValidators();
      this.form.get('accountType')?.updateValueAndValidity();
    }

    if (this.isEditMode && this.data.user) {
      const u = this.data.user;
      this.form.patchValue({
        username:    u.username,
        firstName:   u.firstName,
        lastName:    u.lastName,
        email:       u.email,
        accountType: u.accountType,
      });

      this.form.markAllAsTouched(); // show the errors even its not touched by user
      this.originalValues = this.form.getRawValue();

      const matched = this.accountTypeOptions.find(o => o.value === u.accountType);
      if (matched) {
        this.selectedAccountType = matched;
      }
    }

    this.form.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => { this.apiError = null; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setAccountType(event: { label: string; value: string }): void {
    this.selectedAccountType = event;
    this.form.patchValue({ accountType: event.value });
  }

  getFieldError(field: string): string | null {
    const ctrl = this.form.get(field);
    if (!ctrl || !ctrl.invalid || !ctrl.touched){ return null;}

    if (field === 'username') {
      if (ctrl.hasError('required'))  { return 'Username is required (min. 5 characters).';}
      if (ctrl.hasError('minlength')) { return 'Username is required (min. 5 characters).';}
      if (ctrl.hasError('pattern'))   { return 'Only alphanumeric characters and underscores are allowed.';}
      if (ctrl.hasError('alreadyExists')){ return 'Username already exists.';}
    }
    if (field === 'firstName') {
      if (ctrl.hasError('required')) { return 'First name is required.';}
      if (ctrl.hasError('pattern'))  { return 'Only alphabetical characters are allowed.';}
    }
    if (field === 'lastName') {
      if (ctrl.hasError('required'))  {return 'Last name is required.';}
      if (ctrl.hasError('pattern'))   {return 'Only alphabetical characters are allowed.';}
    }
    if (field === 'email') {
      if (ctrl.hasError('required'))  { return 'Email is required.'; }
      if (ctrl.hasError('email'))    { return 'Invalid email address format.';}
    }
    if (field === 'accountType') {
      if (ctrl.hasError('required')) { return 'Select an account type.';}
    }
    return null;
  }


  get hasChanges(): boolean {
    if (!this.isEditMode || !this.originalValues) {
      return true;
    }
    const current = this.form.getRawValue();
    return JSON.stringify(current) !== JSON.stringify(this.originalValues);
  }

  get isFormValid(): boolean {
    const dropdownValid = this.isEditMode && !this.portalAccountService.isAdmin
      ? true
      : !!this.selectedAccountType;

    return this.form.valid && dropdownValid && this.hasChanges;
  }


  store(): void {
    if (!this.isFormValid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode && !this.data.user) {
      this.toastr.error('No user data provided for editing.', 'Error!');
      return;
    }

    this.spinner.show().catch(console.error);

    // Build payload — exclude username in edit mode since it's disabled
    const raw = this.form.getRawValue();
    const payload: UpdateUserRequest = this.isEditMode
      ? {
          ...(this.portalAccountService.isAdmin && { username: raw.username, accountType: raw.accountType }),
          firstName:   raw.firstName,
          lastName:    raw.lastName,
          email:       raw.email,
        }
      : {
          username:    raw.username,
          firstName:   raw.firstName,
          lastName:    raw.lastName,
          email:       raw.email,
          accountType: raw.accountType,
        };

    const request$ = this.isEditMode && this.data.user
      ? this.portalAccountService.updateUser(this.data.user.uid, payload)
      : this.portalAccountService.createUser(payload as CreateUserRequest);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res.status === 200) {
          const successMessage = this.isEditMode
            ? `Details for ${this.data.user!.username} has been successfully updated!`
            : 'New portal account has been successfully created!';

          const isSelfEdit = this.isEditMode && this.data.user?.uid === this.portalAccountService.currentUserUid;

          if (isSelfEdit) {
            const roleChanged = this.portalAccountService.isAdmin
              && raw.accountType
              && raw.accountType !== this.originalValues?.accountType;

            localStorage.setItem('firstName', raw.firstName);
            localStorage.setItem('lastName',  raw.lastName);
            localStorage.setItem('email',     raw.email);
            if (this.portalAccountService.isAdmin) {
              localStorage.setItem('accountType', raw.accountType);
            }
            this.userService.notifyProfileUpdated();

            if (roleChanged) {
              // Self-edit already wrote the new accountType to localStorage above, so
              // UserStatusCheckerService's poll will never see a mismatch and won't
              // trigger its usual reload — do it here instead, same as checkForRoleChange()
              // does, so RbacService and every permission directive picks up the new role.
              // Toast is deferred past the reload (see APP_INITIALIZER) since it wouldn't
              // survive the reload otherwise.
              sessionStorage.setItem('postReloadToast', successMessage);
              window.location.reload();
              return;
            }
          }

          this.toastr.success(successMessage);
          this.dialogRef.close(true);
        } else if (!this.isEditMode && res.status === 409) {
          this.form.get('username')?.setErrors({ alreadyExists: true });
          this.form.get('username')?.markAsTouched();
          this.apiError = 'Please apply the necessary corrections.';
        } else {
          this.apiError = this.isEditMode
            ? 'Failed to update account. Please try again later.'
            : 'Failed to create account. Please try again later.';
        }

        this.spinner.hide().catch(console.error); 
      },
      complete: () => {
        this.spinner.hide().catch(console.error);
      },
    });
  }
  close(): void {
    this.dialogRef.close(false);
  }

  isInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  touchAccountType(): void {
    this.form.get('accountType')?.markAsTouched();
  }
}
